import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  comparisonForPreset,
  datesForPreset,
  REPORT_PERIOD_PRESETS
} from '../reportFilters';
import {
  effectiveProductOptions,
  reconcileProductCatalogFilters
} from '../reportProductCascade';
import { ReportDimensionFilter } from './ReportDimensionFilter';
import './ReportFilters.scss';

const ORDER_TYPE_VALUES = ['billable', 'direct_allocation'];

function ReportFilters({
  catalogLoading,
  dirty,
  filters,
  onApply,
  onChange,
  onReset,
  options,
  reportMode = 'sales',
  showOrganizations = true,
  validationError
}) {
  const intl = useIntl();
  const activityMode = reportMode === 'activity';
  const hasAdvancedFilters = !activityMode || showOrganizations;
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const update = patch => onChange({ ...filters, ...patch });
  const changeDimensionMode = (modeKey, selectionKey) => mode => update({
    [modeKey]: mode,
    ...(mode === 'all' ? { [selectionKey]: [] } : {})
  });
  const changePreset = preset => update({
    preset,
    ...(preset === 'custom' ? {} : datesForPreset(preset)),
    ...(['previous_period', 'previous_quarter'].includes(filters.compare)
      ? { compare: comparisonForPreset(preset) }
      : {})
  });
  const effectiveProducts = useMemo(() => effectiveProductOptions(
    options.products,
    filters.productGroupMode,
    filters.productGroupIds
  ), [filters.productGroupIds, filters.productGroupMode, options.products]);
  const updateProductCatalog = patch => onChange(reconcileProductCatalogFilters(
    { ...filters, ...patch },
    options.products,
    options.productGroups
  ));
  const changeProductGroupMode = mode => updateProductCatalog({
    productGroupMode: mode,
    ...(mode === 'all' ? { productGroupIds: [] } : {})
  });

  const orderTypeOptions = [
    {
      value: 'billable',
      label: intl.formatMessage({ id: 'components.pages.reports.filters.orderType.billable' })
    },
    {
      value: 'direct_allocation',
      label: intl.formatMessage({ id: 'components.pages.reports.filters.orderType.directAllocation' })
    }
  ];
  const selectedOrderTypes = filters.orderTypeMode === 'all'
    ? []
    : filters.orderTypeMode === 'exclude'
      ? ORDER_TYPE_VALUES.filter(value => !filters.orderTypes.includes(value))
      : ORDER_TYPE_VALUES.filter(value => filters.orderTypes.includes(value));
  const toggleOrderType = value => {
    const next = selectedOrderTypes.includes(value)
      ? selectedOrderTypes.filter(selected => selected !== value)
      : ORDER_TYPE_VALUES.filter(candidate => selectedOrderTypes.includes(candidate) || candidate === value);

    if (next.length === 0) {
      update({ orderTypeMode: 'all', orderTypes: [] });
      return;
    }

    update({ orderTypeMode: 'include', orderTypes: next });
  };
  const advancedCount = (activityMode ? [
    [filters.organizationMode, filters.organizationIds]
  ] : [
    [filters.organizationMode, filters.organizationIds],
    [filters.productGroupMode, filters.productGroupIds],
    [filters.productMode, filters.productIds],
    [filters.orderTypeMode, filters.orderTypes]
  ]).reduce((count, [mode, values], index) => (
    count + ((!showOrganizations && index === 0) || mode === 'all' ? 0 : Math.max(1, values.length))
  ), 0);
  const comparisonValue = ['previous_period', 'previous_quarter'].includes(filters.compare)
    ? 'previous'
    : filters.compare;
  const changeComparison = value => update({
    compare: value === 'previous' ? comparisonForPreset(filters.preset) : value
  });
  const changeCustomDate = patch => update({
    ...patch,
    preset: 'custom',
    ...(filters.compare === 'previous_quarter' ? { compare: 'previous_period' } : {})
  });

  return (
    <section aria-label="Report filters" className="report-filters">
      <div className="report-filters__primary">
        <div className="report-filter-field report-filter-field--period">
          <label htmlFor="report-period"><FormattedMessage id="components.pages.reports.filters.period"/></label>
          <select
            id="report-period"
            onChange={event => changePreset(event.target.value)}
            value={filters.preset}
          >
            {REPORT_PERIOD_PRESETS.map(preset => (
              <FormattedMessage id={`components.pages.reports.filters.period.${preset}`} key={preset}>
                {message => <option value={preset}>{message}</option>}
              </FormattedMessage>
            ))}
          </select>
        </div>
        <div className="report-filter-field">
          <label htmlFor="report-date-from"><FormattedMessage id="components.pages.reports.filters.dateFrom"/></label>
          <input
            id="report-date-from"
            max={filters.dateTo}
            onChange={event => changeCustomDate({ dateFrom: event.target.value })}
            type="date"
            value={filters.dateFrom}
          />
        </div>
        <div className="report-filter-field">
          <label htmlFor="report-date-to"><FormattedMessage id="components.pages.reports.filters.dateTo"/></label>
          <input
            id="report-date-to"
            min={filters.dateFrom}
            onChange={event => changeCustomDate({ dateTo: event.target.value })}
            type="date"
            value={filters.dateTo}
          />
        </div>
        <div className="report-filter-field">
          <label htmlFor="report-granularity"><FormattedMessage id="components.pages.reports.filters.granularity"/></label>
          <select
            id="report-granularity"
            onChange={event => update({ granularity: event.target.value })}
            value={filters.granularity}
          >
            {['auto', 'day', 'week', 'month', 'year'].map(value => (
              <FormattedMessage id={`components.pages.reports.filters.granularity.${value}`} key={value}>
                {message => <option value={value}>{message}</option>}
              </FormattedMessage>
            ))}
          </select>
        </div>
        {!activityMode && <div className="report-filter-field">
          <label htmlFor="report-comparison"><FormattedMessage id="components.pages.reports.filters.comparison"/></label>
          <select
            id="report-comparison"
            onChange={event => changeComparison(event.target.value)}
            value={comparisonValue}
          >
            <FormattedMessage id="components.pages.reports.filters.previousCalendarYear">
              {message => <option value="previous_calendar_year">{message}</option>}
            </FormattedMessage>
            <FormattedMessage id="components.pages.reports.filters.previousPeriod">
              {message => <option value="previous">{message}</option>}
            </FormattedMessage>
            <FormattedMessage id="components.pages.reports.filters.noComparison">
              {message => <option value="none">{message}</option>}
            </FormattedMessage>
          </select>
        </div>}
      </div>

      {hasAdvancedFilters && <button
        aria-controls="report-advanced-filters"
        aria-expanded={advancedOpen}
        className="report-filters__advanced-trigger"
        onClick={() => setAdvancedOpen(open => !open)}
        type="button"
      >
        <span><FormattedMessage id="components.pages.reports.filters.advanced"/></span>
        {advancedCount > 0 && (
          <span aria-label={`${advancedCount}`} className="report-filters__advanced-count">{advancedCount}</span>
        )}
        <i aria-hidden="true" className={`fas fa-chevron-${advancedOpen ? 'up' : 'down'}`}/>
      </button>}

      {hasAdvancedFilters && advancedOpen && <div
        aria-busy={catalogLoading}
        className={`report-filters__dimensions${showOrganizations ? '' : ' report-filters__dimensions--scoped'}`}
        id="report-advanced-filters"
      >
        {showOrganizations && (
          <div
            aria-labelledby="organizations-label"
            className="report-dimension-card report-dimension-card--organizations"
            role="group"
          >
            <ReportDimensionFilter
              id="organizations"
              labelId="components.pages.reports.filters.organizations"
              mode={filters.organizationMode}
              onModeChange={changeDimensionMode('organizationMode', 'organizationIds')}
              onSelectionChange={organizationIds => update({ organizationIds })}
              options={options.organizations}
              selectedIds={filters.organizationIds}
            />
          </div>
        )}
        {!activityMode && <fieldset
          className="report-dimension-card report-dimension-card--order-types report-order-types-filter"
        >
            <legend><FormattedMessage id="components.pages.reports.filters.orderTypes"/></legend>
            <div className="report-order-types-filter__options">
              {orderTypeOptions.map(option => {
                const checked = selectedOrderTypes.includes(option.value);
                return (
                  <label key={option.value}>
                    <input
                      checked={checked}
                      onChange={() => toggleOrderType(option.value)}
                      type="checkbox"
                    />
                    <span>{option.label}</span>
                  </label>
                );
              })}
            </div>
        </fieldset>}
        {!activityMode && <div className="report-dimension-card report-dimension-card--products">
          <fieldset
            aria-describedby="report-products-scope-hint"
            className="report-product-filter-cluster"
          >
            <legend><FormattedMessage id="components.pages.reports.filters.products"/></legend>
            <p className="report-product-filter-cluster__hint" id="report-products-scope-hint">
              <FormattedMessage id="components.pages.reports.filters.productGroupHint"/>
            </p>
            <div className="report-product-filter-cluster__fields">
              <ReportDimensionFilter
                id="product-groups"
                labelId="components.pages.reports.filters.productGroups"
                mode={filters.productGroupMode}
                onModeChange={changeProductGroupMode}
                onSelectionChange={productGroupIds => updateProductCatalog({ productGroupIds })}
                options={options.productGroups}
                selectedIds={filters.productGroupIds}
              />
              <ReportDimensionFilter
                emptyMessageId="components.pages.reports.filters.noProductsForGroups"
                id="products"
                labelId="components.pages.reports.filters.product"
                mode={filters.productMode}
                onModeChange={mode => updateProductCatalog({
                  productMode: mode,
                  ...(mode === 'all' ? { productIds: [] } : {})
                })}
                onSelectionChange={productIds => updateProductCatalog({ productIds })}
                options={effectiveProducts}
                selectedIds={filters.productIds}
              />
            </div>
          </fieldset>
        </div>}
      </div>}

      <div className="report-filters__footer">
        <div aria-live="polite" className="report-filters__status">
          {validationError && <FormattedMessage id={validationError}/>}
          {!validationError && dirty && <FormattedMessage id="components.pages.reports.filters.notApplied"/>}
        </div>
        <button className="btn btn-outline-primary" onClick={onReset} type="button">
          <FormattedMessage id="components.pages.reports.filters.reset"/>
        </button>
        <button className="btn btn-primary" disabled={!dirty || Boolean(validationError)} onClick={onApply} type="button">
          <FormattedMessage id="components.pages.reports.filters.apply"/>
        </button>
      </div>
    </section>
  );
}

export { ReportFilters };
